import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Pagination,
  InlineLoading,
  InlineNotification,
  Tag,
  Modal,
  Grid,
  Column,
  Dropdown,
  Button,
  TextArea,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  OverflowMenu,
  OverflowMenuItem,
} from "@carbon/react";

import { Filter, Reset } from "@carbon/icons-react";
import { callMongoApi } from "../../../services/mongoApiService";
import {
  addComment,
  updateComment,
} from "../../../services/mongoApiService";

// Headers for the table
const headers = [
  { key: "id", header: "Request ID" },
  { key: "requestType", header: "Request Type" },
  { key: "status", header: "Status" },
  { key: "createdBy", header: "Created By" },
  { key: "createdAt", header: "Created At" },
  { key: "verifiedBy", header: "Verified By" },
  { key: "verifiedAt", header: "Verified At" },
  { key: "actions", header: "Actions" },
];

// Valid tag types for Carbon components
const VALID_TAG_TYPES = ["red", "magenta", "purple", "blue", "cyan", "teal", "green", "gray", "cool-gray", "warm-gray", "high-contrast", "outline"];

// Helper function to get valid tag type
const getValidTagType = (type) => {
  return VALID_TAG_TYPES.includes(type) ? type : "gray";
};

// Helper function to get status tag type
const getStatusTagType = (status) => {
  switch (status?.toLowerCase()) {
    case "pending":
      return "yellow";
    case "in_progress":
      return "blue";
    case "resolved":
      return "green";
    case "closed":
      return "gray";
    case "rejected":
      return "red";
    default:
      return "gray";
  }
};

// Helper function to get request type tag
const getRequestTypeTag = (type) => {
  switch (type?.toUpperCase()) {
    case "CONTACT_OWNER":
      return { type: "purple", text: "Contact Owner" };
    case "SCHEDULE_VIEWING":
      return { type: "cyan", text: "Schedule Viewing" };
    case "ASK_QUESTION":
      return { type: "blue", text: "Ask Question" };
    case "REPORT_ISSUE":
      return { type: "red", text: "Report Issue" };
    case "GENERAL_INQUIRY":
      return { type: "teal", text: "General Inquiry" };
    default:
      return { type: "gray", text: type || "Unknown" };
  }
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  const match = dateString.match(
    /^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}):(\d{2})$/
  );

  if (match) {
    const [, day, month, year, hour, minute, second] = match;

    const date = new Date(
      year,
      month - 1,
      day,
      hour,
      minute,
      second
    );

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // fallback for ISO dates
  const date = new Date(dateString);

  if (isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Parse and format dates in "DD-MM-YYYY HH:mm:ss" format (used by comments API)
const formatCommentDate = (dateString) => {
  if (!dateString) return "N/A";

  const match = dateString.match(/^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}):(\d{2})$/);
  if (!match) return formatDate(dateString); // fall back for other formats

  const [, day, month, year, hour, minute, second] = match;
  const date = new Date(year, month - 1, day, hour, minute, second);

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function HelpRequestsTable() {
  const mountedRef = useRef(true);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pagination and Search states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  // Filter states
  const [requestTypeFilter, setRequestTypeFilter] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  // Dynamic filter options
  const [requestTypeOptions, setRequestTypeOptions] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);

  // ------------------ Search Logic ------------------
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim().toLowerCase()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Extract unique filter options from data
  const extractFilterOptions = useCallback((helpRequests) => {
    const types = new Set();

    helpRequests.forEach(request => {

      if (request.requestType) {
        types.add(request.requestType);
      }
    });
    // Build request type options
    const typeOpts = [{ id: "all", text: "All Types" }];
    Array.from(types).sort().forEach(type => {
      const label = type.replace(/_/g, ' ');
      typeOpts.push({ id: type, text: label });
    });
    setRequestTypeOptions(typeOpts);
  }, []);

  // Normalize help request data
const normalizeHelpRequest = useCallback((item) => ({
  id: String(item.id || item.requestId || item.helpRequestId || item._id || ""),

  propertyId: item.propertyId || item.property?.id,

  propertyTitle: item.propertyTitle || item.property?.title || "N/A",

  propertyAddress:
    item.propertyAddress ||
    [
      item.property?.location?.area_name,
      item.property?.location?.village,
      item.property?.location?.district,
      item.property?.location?.state,
    ]
      .filter(Boolean)
      .join(", ") ||
    "N/A",

  userId: item.userId,

  userName:
    item.userName ||
    item.user?.name ||
    item.owner?.name ||
    "N/A",

  userEmail:
    item.userEmail ||
    item.user?.email ||
    item.owner?.email ||
    "N/A",

  userPhone:
    item.userPhone ||
    item.user?.phone ||
    item.owner?.contact ||
    "N/A",

  requestType: item.requestType || "GENERAL_INQUIRY",

  message: item.message || "No message provided",

  preferredContactMethod: item.preferredContactMethod || "email",

  preferredTime: item.preferredTime || "Not specified",

  status: item.status || "PENDING",

  additionalDetails: item.additionalDetails || {},

  createdBy:
    item.createdBy ||
    item.createdByName ||
    item.userName ||
    item.user?.name ||
    "N/A",

  createdAt: item.createdAt || item.created_at,

  verifiedBy:
    item.verifiedBy ||
    item.respondedBy ||
    item.updatedBy ||
    "N/A",

  verifiedAt:
    item.verifiedAt ||
    item.respondedAt ||
    item.resolvedAt ||
    item.updatedAt ||
    item.updated_at,

  updatedAt: item.updatedAt || item.updated_at,

  adminResponse: item.adminResponse,

  resolvedAt: item.resolvedAt,

  commentCount: item.commentCount || 0,

  totalReplies: item.totalReplies || 0,

  isGeneralInquiry: item.isGeneralInquiry,

  isPartner: item.isPartner,

  partnerType: item.partnerType,

  partnerStatus: item.partnerStatus,

  companyName: item.companyName,

  source: item.source,

  contactPerson: item.contactPerson,

  originalData: item,
}), []);

const fetchTabCounts = useCallback(async () => {
  try {
    const [pendingRes, progressRes, resolvedRes] = await Promise.all([
  callMongoApi(
    "GET",
    "/help-requests/admin/all-requests?status=PENDING&page=1&limit=1"
  ),
  callMongoApi(
    "GET",
    "/help-requests/admin/all-requests?status=IN_PROGRESS&page=1&limit=1"
  ),
  callMongoApi(
    "GET",
    "/help-requests/admin/all-requests?status=RESOLVED&page=1&limit=1"
  ),
]);

    setTabCounts({
  pending: pendingRes?.data?.pagination?.total || 0,
  inProgress: progressRes?.data?.pagination?.total || 0,
  resolved: resolvedRes?.data?.pagination?.total || 0,
});
  } catch (err) {
    console.error("Failed to fetch tab counts", err);
  }
}, []);

  // Fetch help requests (GET only)
const fetchHelpRequests = useCallback(async () => {
  if (!mountedRef.current) return;

  setLoading(true);
  setError("");

  try {
    const statusMap = [
  "PENDING",
  "IN_PROGRESS",
  "RESOLVED",
];
    const currentStatus = statusMap[selectedTab];

    console.log("Selected Tab:", selectedTab);
    console.log("Fetching Status:", currentStatus);

const response = await callMongoApi(
  "GET",
  `/help-requests/admin/all-requests?status=${currentStatus}&page=${page}&limit=${pageSize}`
);

    console.log("API Response:", response);
    console.log("First Request:", response?.data?.requests?.[0]);
console.log("Created At:", response?.data?.requests?.[0]?.createdAt);

    if (
      response?.success &&
      Array.isArray(response?.data?.requests)
    ) {
      const normalizedData =
  response.data.requests.map(normalizeHelpRequest);

setRequests(normalizedData);

setTotalItems(response.data.pagination?.total || 0);

      // IMPORTANT:
      // Build filters from the currently fetched requests
      extractFilterOptions(response.data.requests);
    } else {
      throw new Error("Invalid response format");
    }
  } catch (error) {
    console.error("Failed to load help requests:", error);

    setError("Failed to load help requests. Please try again.");
    setRequests([]);
  } finally {
    if (mountedRef.current) {
      setLoading(false);
    }
  }
}, [
  selectedTab,
  page,
  pageSize,
  normalizeHelpRequest,
  extractFilterOptions,
]);
useEffect(() => {
  mountedRef.current = true;

  fetchHelpRequests();
  fetchTabCounts();

  return () => {
    mountedRef.current = false;
  };
}, [fetchHelpRequests, fetchTabCounts ]);

  // Reset all filters
  const resetFilters = () => {
    setRequestTypeFilter(null);
    setSearchTerm("");
    setDebouncedSearch("");
  };

  // Filter rows based on all filters
  const filteredRows = useMemo(() => {
    let filtered = requests;

    if (debouncedSearch) {
      filtered = filtered.filter(request =>
        (request.propertyTitle?.toLowerCase() || '').includes(debouncedSearch) ||
        (request.userName?.toLowerCase() || '').includes(debouncedSearch) ||
        (request.message?.toLowerCase() || '').includes(debouncedSearch) ||
        (request.requestType?.toLowerCase() || '').includes(debouncedSearch) ||
        (request.id?.toLowerCase() || '').includes(debouncedSearch)
      );
    }

    if (requestTypeFilter && requestTypeFilter.id !== "all") {
      filtered = filtered.filter(request =>
        request.requestType?.toLowerCase() === requestTypeFilter.id.toLowerCase()
      );
    }

    return filtered;
  }, [requests, debouncedSearch, requestTypeFilter]);
const tabFilteredRows = filteredRows;
const [tabCounts, setTabCounts] = useState({
  pending: 0,
  inProgress: 0,
  resolved: 0,
});
//const tabFilteredRows = useMemo(() => {
  //switch (selectedTab) {
    //case 0:
      //return filteredRows.filter(
        //(r) => r.status?.toLowerCase() === "new"
      //);

    //case 1:
      //return filteredRows.filter(
        //(r) => r.status?.toLowerCase() === "pending"
      //);

    //case 2:
      //return filteredRows.filter(
        //(r) => r.status?.toLowerCase() === "complete"
      //);

    //default:
      //return filteredRows;
  //}
//}, [filteredRows, selectedTab]);
  
const paginatedRows = tabFilteredRows;

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [ requestTypeFilter, debouncedSearch]);

  // Count active filters
  const activeFilterCount = [
    requestTypeFilter && requestTypeFilter.id !== "all",
    !!debouncedSearch
  ].filter(Boolean).length;
const fetchComments = useCallback(async (requestId) => {
  if (!requestId) return;

  setCommentsLoading(true);
  setCommentsError("");

  try {
    const response = await callMongoApi(
      "GET",
      `/help-requests/${requestId}`
    );

    if (response?.success) {
      setComments(response.data?.comments || []);
    } else {
      setComments([]);
    }
  } catch (err) {
    console.error("Failed to load comments:", err);
    setCommentsError("Failed to load comments.");
  } finally {
    setCommentsLoading(false);
  }
}, []);

useEffect(() => {
    if (isDetailModalOpen && selectedRequest?.id) {
      fetchComments(selectedRequest.id);

      // Fetch full details in case the list endpoint omitted user info
      callMongoApi("GET", `/help-requests/${selectedRequest.id}`)
        .then((response) => {
          if (response?.success && response.data && mountedRef.current) {
            setSelectedRequest((prev) => ({
              ...prev,
              ...normalizeHelpRequest(response.data),
            }));
          }
        })
        .catch(() => {
          // silently ignore — fall back to list data already shown
        });
    }
    if (!isDetailModalOpen) {
      setComments([]);
      setReplyText("");
      setCommentsError("");
    }
  }, [isDetailModalOpen, selectedRequest?.id, fetchComments, normalizeHelpRequest]);

const handlePostComment = async () => {
  if (!replyText.trim() || !selectedRequest?.id) return;

  setReplySubmitting(true);
  setCommentsError("");

  try {
    if (editingCommentId) {
      // Update existing comment
      await updateComment(
        selectedRequest.id,
        editingCommentId,
        replyText.trim()
      );

      setEditingCommentId(null);
    } else {
      // Add new comment
      await addComment(
        selectedRequest.id,
        replyText.trim()
      );
    }

    setReplyText("");
    await fetchComments(selectedRequest.id);

  } catch (err) {
    console.error(err);
    setCommentsError("Failed to save comment. Please try again.");
  } finally {
    setReplySubmitting(false);
  }
};

const handleCancel = () => {
  setIsDetailModalOpen(false);
};

const handleUpdate = async () => {
  if (!selectedRequest?.id) {
    setIsDetailModalOpen(false);
    return;
  }

  setReplySubmitting(true);
  setCommentsError("");

  try {
    // Update status — now includes adminResponse so backend can record verifiedBy
    await callMongoApi(
      "PUT",
      `/help-requests/admin/${selectedRequest.id}/status`,
      {
        status: selectedStatus,
        adminResponse: replyText.trim() || "Status updated",
      }
    );

    // Still add it as a comment too, so it shows in the comments thread
    if (replyText.trim()) {
      await addComment(selectedRequest.id, replyText.trim());
      setReplyText("");
    }

    await fetchHelpRequests();
    setIsDetailModalOpen(false);
  } catch (err) {
    setCommentsError("Failed to update request. Please try again.");
  } finally {
    setReplySubmitting(false);
  }
};

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setSelectedStatus(request.status);
    setIsDetailModalOpen(true);
  };

  

  return (
    <>
      {/* Filter Section */}
      <div style={{ 
        padding: "1rem", 
        backgroundColor: "#f4f4f4", 
        borderRadius: "8px", 
        marginBottom: "1rem",
        border: "1px solid #e0e0e0"
      }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}>
          <Filter style={{ marginRight: "0.5rem" }} />
          <h4 style={{ margin: 0, flex: 1 }}>Filter Help Requests</h4>
          {activeFilterCount > 0 && (
            <Button
              kind="ghost"
              renderIcon={Reset}
              size="sm"
              onClick={resetFilters}
            >
              Reset Filters ({activeFilterCount})
            </Button>
          )}
        </div>
        
        <Grid style={{ padding: 0 }}>
          <Column lg={6} md={6} sm={6}>
            <Dropdown
              id="request-type-filter"
              items={requestTypeOptions}
              titleText="Request Type"
              label="Choose"
              itemToString={(item) => item?.text || ""}
              selectedItem={requestTypeFilter}
              onChange={({ selectedItem }) => setRequestTypeFilter(selectedItem)}
              size="sm"
            />
          </Column>
        </Grid>
      </div>
<Tabs
  selectedIndex={selectedTab}
  onChange={({ selectedIndex }) => {
    setSelectedTab(selectedIndex);
    setPage(1);
  }}
>
<TabList>
  <Tab>
    Pending{" "}
    <Tag type="warm-gray">
      {tabCounts.pending}
    </Tag>
  </Tab>

  <Tab>
    In Progress{" "}
    <Tag type="blue">
      {tabCounts.inProgress}
    </Tag>
  </Tab>

  <Tab>
    Resolved{" "}
    <Tag type="green">
      {tabCounts.resolved}
    </Tag>
  </Tab>
</TabList>

    <TabPanels>
        <TabPanel/>
        <TabPanel/>
        <TabPanel/>
    </TabPanels>

</Tabs>

      {/* DataTable Container */}
      <DataTable rows={paginatedRows} headers={headers}>
        {({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => {
          return (
            <TableContainer title="Help Requests">
              <TableToolbar>
                <TableToolbarContent>
                  <TableToolbarSearch
                    expanded
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by property, user, message, or request type..."
                    value={searchTerm}
                  />
                </TableToolbarContent>
              </TableToolbar>

              {/* Loading and Error Messages */}
              {loading && (
                <div style={{ padding: "2rem", textAlign: "center" }}>
                  <InlineLoading description="Loading help requests..." />
                </div>
              )}

              {error && !loading && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="error"
                    title="Error"
                    subtitle={error}
                    lowContrast
                    onClose={() => setError("")}
                  />
                </div>
              )}

              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map((header) => {
                      const headerProps = getHeaderProps({ header });
                      const { key: headerKey, ...restHeaderProps } = headerProps;
                      return (
                        <TableHeader 
                          key={header.key}
                          {...restHeaderProps}
                        >
                          {header.header}
                        </TableHeader>
                      );
                    })}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loading && paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length} style={{ textAlign: 'center', padding: '2rem' }}>
                        {activeFilterCount > 0 || searchTerm
                          ? 'No results found. Try adjusting your search or filters.' 
                          : 'No help requests found.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row) => {
                      const rowProps = getRowProps({ row });
                      const { key: rowKey, ...restRowProps } = rowProps;
                      const requestTypeConfig = getRequestTypeTag(row.requestType);
                      const statusType = getStatusTagType(row.status);
                      
                      return (
                        <TableRow key={row.id} {...restRowProps}>
                          <TableCell>
  <span style={{ fontWeight: 500 }}>
    #{row.id}
  </span>
</TableCell>
                          <TableCell>
                            <Tag type={getValidTagType(requestTypeConfig.type)}>
                              {requestTypeConfig.text}
                            </Tag>
                          </TableCell>
                          <TableCell>
                            <Tag type={getValidTagType(statusType)}>
                              {row.status?.replace(/_/g, ' ') || 'N/A'}
                            </Tag>
                          </TableCell>
                          <TableCell>
  {row.createdBy || "N/A"}
</TableCell>

<TableCell>
  {formatDate(row.createdAt)}
</TableCell>

<TableCell>
  {row.verifiedBy || "N/A"}
</TableCell>

<TableCell>
  {row.verifiedAt ? formatDate(row.verifiedAt) : "N/A"}
</TableCell>

<TableCell>
  <OverflowMenu
    aria-label={`Actions for request ${row.id}`}
    flipped
    size="sm"
  >
    <OverflowMenuItem
      itemText="View Details"
      onClick={() => handleViewDetails(row)}
    />
  </OverflowMenu>
</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      </DataTable>

      {/* Pagination */}
      {tabFilteredRows.length > 0 && !loading && (
        <div style={{ padding: "1rem 1.25rem", display: "flex", justifyContent: "flex-end" }}>
         <Pagination
  totalItems={totalItems}
  page={page}
  pageSize={pageSize}
  pageSizes={[5, 10, 20, 50, 100]}
  onChange={({ page: p, pageSize: ps }) => {
    setPage(p);
    setPageSize(ps);
  }}
/>
        </div>
      )}

      {/* Help Request Details Modal */}
      <Modal
        open={isDetailModalOpen}
        modalHeading="Help Request Details"
        secondaryButtonText="Cancel"
        primaryButtonText={replySubmitting ? "Updating..." : "Update"}
        onRequestClose={handleCancel}
        onSecondarySubmit={handleCancel}
        onRequestSubmit={handleUpdate}
        primaryButtonDisabled={replySubmitting}
        size="lg"
      >
        {selectedRequest && (
          <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: "8px" }}>
            <Grid>

              <Column lg={6}>
  <div style={{ marginBottom: "1rem" }}>
    <strong
      style={{
        display: "block",
        marginBottom: "0.5rem",
      }}
    >
      Verification Status:
    </strong>

    <Dropdown
      id="verification-status"
      label="Select Status"
      hideLabel
      items={[
        { id: "PENDING", text: "Pending" },
        { id: "IN_PROGRESS", text: "In Progress" },
        { id: "RESOLVED", text: "Resolved" },
      ]}
      itemToString={(item) => item?.text || ""}
      selectedItem={[
        { id: "PENDING", text: "Pending" },
        { id: "IN_PROGRESS", text: "In Progress" },
        { id: "RESOLVED", text: "Resolved" },
      ].find((item) => item.id === selectedStatus)}
      onChange={({ selectedItem }) => {
        setSelectedStatus(selectedItem.id);
      }}
    />
  </div>
</Column>

              <Column lg={16}>
                <div style={{ marginBottom: "0.75rem" }}>
                  <strong>Message:</strong>
                  <div style={{ marginTop: "4px", padding: "0.75rem", backgroundColor: "#f4f4f4", borderRadius: "4px" }}>
                    {selectedRequest.message}
                  </div>
                </div>
              </Column>
              <Column lg={8}>
                <div style={{ marginBottom: "0.75rem" }}>
                  <strong>Preferred Contact Method:</strong>
                  <div style={{ marginTop: "4px" }}>
                    <Tag type="blue">
                      {selectedRequest.preferredContactMethod?.toUpperCase() || "Email"}
                    </Tag>
                  </div>
                </div>
              </Column>
              <Column lg={8}>
                <div style={{ marginBottom: "0.75rem" }}>
                  <strong>Preferred Time:</strong>
                  <div style={{ marginTop: "4px" }}>{selectedRequest.preferredTime}</div>
                </div>
              </Column>
              <Column lg={8}>
                <div style={{ marginBottom: "0.75rem" }}>
                  <strong>Name:</strong>
                  <div style={{ marginTop: "4px" }}>{selectedRequest.userName}</div>
                </div>
              </Column>
              <Column lg={8}>
                <div style={{ marginBottom: "0.75rem" }}>
                  <strong>Email:</strong>
                  <div style={{ marginTop: "4px" }}>{selectedRequest.userEmail}</div>
                </div>
              </Column>

              {commentsError && (
                <Column lg={16}>
                  <InlineNotification
                    kind="error"
                    title="Error"
                    subtitle={commentsError}
                    lowContrast
                    onClose={() => setCommentsError("")}
                    style={{ marginBottom: "0.75rem" }}
                  />
                </Column>
              )}


{/* Comment box — shows the latest comment for editing, or an empty box for a new one */}
              <Column lg={16}>
                <h5 style={{ marginBottom: "0.5rem", borderBottom: "1px solid #e0e0e0", paddingBottom: "0.5rem" }}>
                  Comments
                </h5>
              </Column>
              <Column lg={16}>
                {commentsLoading ? (
                  <div style={{ padding: "1rem 0" }}>
                    <InlineLoading description="Loading comment..." />
                  </div>
                ) : (
                  <div
                    style={{
                      border: "1px solid #d0d0d0",
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                      overflow: "hidden",
                      marginBottom: "1.5rem",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    }}
                  >
                   {comments.length > 0 ? (
  comments.map((comment) => (
    <div
      key={comment.commentId}
      style={{
        borderBottom: "1px solid #e0e0e0",
        padding: "12px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "12px",
          color: "#6f6f6f",
          marginBottom: "6px",
        }}
      >
        <span>
          Created: {formatCommentDate(comment.createdAt)}
        </span>

        <span>
          Updated: {formatCommentDate(comment.updatedAt)}
        </span>
      </div>

      <div style={{ fontWeight: "600" }}>
        {comment.user?.name}
      </div>

      <div style={{ marginTop: "4px" }}>
        {comment.text}
      </div>
      <Button
  kind="ghost"
  size="sm"
  onClick={() => {
    setEditingCommentId(comment.commentId);
    setReplyText(comment.text);
  }}
>
  Edit
</Button>
    </div>
  ))
) : (
  <div style={{ padding: "12px" }}>
    No comments found.
  </div>
)}
                    <TextArea
                      id="comment-box"
                      labelText=""
                      hideLabel
                      placeholder="Type your comment"
                      rows={3}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      disabled={replySubmitting}
                      style={{ border: "none", boxShadow: "none", backgroundColor: "transparent" }}
                    />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        padding: "0.5rem 0.75rem",
                        backgroundColor: "#f4f4f4",
                        borderTop: "1px solid #e0e0e0",
                      }}
                    >
                      <Button
                        kind="primary"
                        size="sm"
                        onClick={handlePostComment}
                        disabled={replySubmitting || !replyText.trim()}
                      >
                       { replySubmitting
  ? "Saving..."
  : editingCommentId
    ? "Update"
    : "Post"}
                      </Button>
                    </div>
                  </div>
                )}
              </Column>
            </Grid>
          </div>
        )}
      </Modal>
    </>
  );
}